import {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {api, errorMessage} from '../lib/api';

// Only retry read-only public requests. Never replay investor submissions or auth.
export function usePublicCollection(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const activeRequest = useRef(null);

  const reload = useCallback(async () => {
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setLoading(true);
    setError('');
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await api.get(path, {signal: controller.signal, timeout: 12000});
          if (!Array.isArray(response.data)) {
            throw new Error('The server returned an unexpected response. Please try again.');
          }
          if (!controller.signal.aborted) setData(response.data);
          return;
        } catch (failure) {
          if (axios.isCancel(failure) || controller.signal.aborted) return;
          const status = failure.response?.status;
          const transient = !failure.response || [408, 429, 500, 502, 503, 504].includes(status);
          if (!transient || attempt === 2) throw failure;
          await new Promise(resolve => setTimeout(resolve, [500, 1500][attempt]));
          if (controller.signal.aborted) return;
        }
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(axios.isAxiosError(failure) ? errorMessage(failure) : failure.message);
      }
    } finally {
      if (activeRequest.current === controller && !controller.signal.aborted) setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    reload();
    return () => activeRequest.current?.abort();
  }, [reload]);

  return {data, loading, error, reload};
}